<template>
  <div class="ticker-popover" role="dialog" :aria-label="title" :style="{ left: `${left}px`, width: `${width}px` }">
    <span class="ticker-popover__arrow" :style="{ left: `${arrowLeft}px` }"></span>

    <header class="ticker-popover__header">
      <button
        v-if="showBack"
        type="button"
        class="ticker-popover__back"
        :aria-label="$t('Back')"
        @click="$emit('back')"
      >
        ‹
      </button>
      <!--
        No icon beside the title: the design starts it at the panel's own 12px
        column, and the trigger in the ticker is framed while its popover is
        open, so the association is already visible.
      -->
      <h2 class="ticker-popover__title">{{ title }}</h2>
      <button type="button" class="ticker-popover__close" :aria-label="$t('Close')" @click="$emit('close')">
        <img src="@/assets/images/quick-play-close.svg" alt="" />
      </button>
    </header>

    <div ref="body" class="ticker-popover__body" @scroll="syncFade">
      <slot />
    </div>
    <!--
      Only while there is more below. Shown unconditionally it dimmed the last
      rows of a list that was already complete, which reads as content cut off
      with no way to reach it.
    -->
    <span v-if="hasMoreBelow" class="ticker-popover__fade"></span>
  </div>
</template>

<script>
/*
 * The tooltip shell the desktop ticker's icons open (notifications, schedule)
 * in place of the v1 full-height menu modal: header, scrolling body, and an
 * arrow pointing back at the icon. Positioned by the parent, which knows where
 * that icon sits.
 */
export default {
  name: 'TickerPopover',
  props: {
    title: {
      type: String,
      required: true,
    },
    // Shows a back button before the title (e.g. a detail view).
    showBack: {
      type: Boolean,
      default: false,
    },
    width: {
      type: Number,
      default: 244,
    },
    // Popover's left edge, relative to the positioned parent.
    left: {
      type: Number,
      default: 0,
    },
    // Arrow tip, relative to the popover's left edge.
    arrowLeft: {
      type: Number,
      default: 24,
    },
  },
  data() {
    return {
      hasMoreBelow: false,
      resizeObserver: null,
    };
  },
  mounted() {
    document.addEventListener('keydown', this.onKeydown);
    this.$nextTick(this.syncFade);
    // The list arrives (and grows) after mount, and the detail view swaps the
    // whole body out, so the fade follows the content rather than the mount.
    if (typeof ResizeObserver !== 'undefined' && this.$refs.body) {
      this.resizeObserver = new ResizeObserver(this.syncFade);
      this.resizeObserver.observe(this.$refs.body);
      if (this.$refs.body.firstElementChild) this.resizeObserver.observe(this.$refs.body.firstElementChild);
    }
  },
  updated() {
    this.syncFade();
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.onKeydown);
    if (this.resizeObserver) this.resizeObserver.disconnect();
  },
  methods: {
    onKeydown(event) {
      if (event.key === 'Escape') this.$emit('close');
    },
    syncFade() {
      const body = this.$refs.body;
      if (!body) return;
      // 1px of slack: fractional layout can leave scrollHeight a hair above
      // clientHeight on a list that is in fact complete.
      this.hasMoreBelow = body.scrollHeight - body.clientHeight - body.scrollTop > 1;
    },
  },
};
</script>

<style lang="scss" scoped>
/*
 * Measured from Figma node 763:1179 (the 244x280 notification popover): the
 * panel is the same near-black the header and rail cards use, outlined in the
 * gold accent at 60%, and the only rule inside is the one between items.
 */
$border: rgba(239, 200, 127, 0.6);
$bg: #030f15;
$divider: #19253c;

.ticker-popover {
  position: absolute;
  top: calc(100% + 4px);
  z-index: 1100;
  max-height: min(420px, calc(100vh - 140px));
  border: 1px solid $border;
  border-radius: 8px;
  background: $bg;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.5);
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  // The header it opens from is `nowrap`; the popover's text wraps.
  white-space: normal;
}

.ticker-popover__arrow {
  position: absolute;
  top: -7px;
  width: 12px;
  height: 12px;
  margin-left: -6px;
  border-top: 1px solid $border;
  border-left: 1px solid $border;
  background: $bg;
  transform: rotate(45deg);
}

/*
 * 12px inset all round, and no rule under the title -- the design separates
 * items from each other, not the header from the list.
 */
.ticker-popover__header {
  flex: 0 0 auto;
  padding: 12px 12px 0;
  display: flex;
  align-items: center;
  gap: 8px;
}

.ticker-popover__back {
  flex: 0 0 18px;
  width: 18px;
  height: 18px;
  padding: 0;
  border: 0;
  background: none;
  color: #d9b977;
  font-size: 22px;
  line-height: 16px;
  cursor: pointer;
}

.ticker-popover__title {
  flex: 1 1 auto;
  min-width: 0;
  margin: 0;
  overflow: hidden;
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-overflow: ellipsis;
  text-transform: uppercase;
  white-space: nowrap;
}

.ticker-popover__close {
  flex: 0 0 16px;
  width: 16px;
  height: 16px;
  padding: 0;
  border: 0;
  background: none;
  cursor: pointer;

  img {
    width: 100%;
    height: 100%;
    display: block;
  }
}

.ticker-popover__body {
  position: relative;
  flex: 1 1 auto;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  overscroll-behavior: contain;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
}

/*
 * The design fades the last rows into the panel rather than cutting them off
 * (Rectangle 18823), so there is a visible hint that the list continues. It
 * sits over the scrolling body, so it must not swallow clicks.
 */
.ticker-popover__fade {
  position: absolute;
  left: 1px;
  right: 1px;
  bottom: 1px;
  height: 64px;
  border-radius: 0 0 7px 7px;
  background: linear-gradient(180deg, rgba(3, 15, 21, 0) 0%, #030f15 85%);
  pointer-events: none;
}
</style>
